import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { PortalBorrowerAchAccountFragment } from '@willow/graphql-iso/src/portal';
import { Loader, paymentAmountFormat } from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { BorrowerAchAccountId, LoanId } from '@willow/types-iso';

import { AutopayContext } from '../../contexts/autopayContext';
import { WillowModal } from '../modal/Modal';

import './AutopayModal.scss';

interface Props {
  loanId: LoanId;
  pastDueBalance: number | undefined;
  achAccounts: PortalBorrowerAchAccountFragment[];
}

export const ConfirmNachaAutopayModal = ({ loanId, pastDueBalance, achAccounts }: Props) => {
  const enabledAccounts = achAccounts.filter((a) => !a.disabledAt);
  const [selectedAccountId, setSelectedAccountId] = useState<BorrowerAchAccountId | undefined>(
    enabledAccounts.at(0)?.id,
  );
  const {
    showConfirmAutopayModal,
    setShowConfirmAutopayModal,
    updateNachaAutopaySettings,
    updateNachaAutopaySettingsLoading,
  } = useContext(AutopayContext);

  useEffect(() => {
    // When user adds an ACH account, default to the first enabled one
    if (selectedAccountId == null && enabledAccounts.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedAccountId(enabledAccounts.at(0)?.id);
    }
  }, [enabledAccounts, selectedAccountId]);

  const resetModal = () => {
    setShowConfirmAutopayModal(false);
  };

  const onSubmit = async () => {
    try {
      await updateNachaAutopaySettings(loanId, selectedAccountId);
      resetModal();
      toast.success('Auto-pay was successfully enabled');
    } catch (_err: unknown) {
      toast.error('There was an issue updating auto-pay. Please refresh the page and try again.');
    }
  };

  return (
    <WillowModal showModal={showConfirmAutopayModal} onClose={resetModal}>
      {enabledAccounts.length ? (
        <div className="modal-autopay">
          <h2>Enable Auto-Pay?</h2>

          <div className="modal-autopay__copy">
            {enabledAccounts.length > 1 && (
              <Form.Group>
                <Form.Label>Confirm bank account to enroll in auto-pay.</Form.Label>
                <Form.Select
                  onChange={(e) => setSelectedAccountId(e.target.value as BorrowerAchAccountId)}
                  value={selectedAccountId}
                >
                  {enabledAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.accountName} ({account.lastFour})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {pastDueBalance ? (
              <>
                <p className="mb-4">
                  Your loan is currently past due and {paymentAmountFormat(pastDueBalance)} is outstanding. You can
                  confirm immediate payment by pressing the “submit payment” button on the payment page.
                </p>
                <p>
                  Enabling auto-pay will deduct any remaining unpaid balance (including any fees or past due amount) due
                  on your next due date.
                </p>
              </>
            ) : (
              <>
                <p className="mb-4">
                  Enabling auto-pay will deduct any remaining unpaid balance (including any fees or past due amount) due
                  on your due date.
                </p>
                <p>
                  You can still submit a payment manually by confirming the amount you wish to pay and pressing the
                  "submit payment" button. Any remaining amount will be auto-deducted on your due date if auto-payment
                  is enabled.
                </p>
              </>
            )}
          </div>

          <div className="modal-autopay__buttons">
            <button
              type="button"
              onClick={onSubmit}
              className="modal-autopay__submit-btn"
              disabled={updateNachaAutopaySettingsLoading || !selectedAccountId}
            >
              Enable Auto-Pay
              {updateNachaAutopaySettingsLoading && <Loader />}
            </button>
            <button type="button" onClick={resetModal} className="modal-autopay__cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="modal-autopay">
          <h2>No Eligible Bank Account</h2>

          <div className="modal-autopay__copy">
            You need at least one active bank account to enable auto-pay. Please add a bank account to continue.
          </div>

          <div className="modal-autopay__buttons">
            <button type="button" onClick={resetModal} className="modal-autopay__submit-btn">
              Close
            </button>
          </div>
        </div>
      )}
    </WillowModal>
  );
};
